import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../hooks/useAuth';
import { AuthLayout } from '../../components/layouts/AuthLayout';
import AuthStepIndicator from '../../components/auth/AuthStepIndicator';
import { buildAcceptedDocuments } from '../../config/legal';
import { validateNewPassword } from '../../utils/passwordStrength';
import { clearGoogleGetStarted, peekGoogleGetStarted } from '../../utils/googleGetStarted';
import StepAccount from './signup/StepAccount';
import StepOrganization from './signup/StepOrganization';
import StepLegal from './signup/StepLegal';
import {
  EMPTY_SIGNUP_WIZARD_DATA,
  SIGNUP_STEPS,
  SIGNUP_STEP_HEADINGS,
  SIGNUP_STEP_LABELS,
  SIGNUP_STEP_TOTAL,
  resolveSignupApiError,
  type GoogleProfilePrefill,
  type SignupConsentField,
  type SignupStep,
  type SignupWizardData,
  type SignupWizardErrors,
} from './signup/types';
import { slideUp } from '@/lib/motion';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PRIMARY_BUTTON_CLASS =
  'flex-1 h-12 rounded-full bg-primary-500 text-white font-bold text-[15px] hover:bg-primary-600 dark:hover:bg-primary-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

const BACK_BUTTON_CLASS =
  'h-12 shrink-0 px-6 rounded-full border border-border-primary/20 text-text-primary font-bold text-[15px] hover:border-primary-500 hover:text-primary-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

/**
 * Rules for one step, in one place.
 *
 * `googleLinked` skips step 1 wholesale: the verified id_token carries the
 * identity, none of these fields is part of that payload, and they render
 * read-only — so demanding e.g. a last name the Google token never supplied
 * would be an unfixable dead end.
 */
const validateStep = (
  step: SignupStep,
  data: SignupWizardData,
  googleLinked: boolean
): SignupWizardErrors => {
  const errors: SignupWizardErrors = {};

  if (step === 1 && !googleLinked) {
    if (!data.firstName.trim()) errors.firstName = 'First name is required';
    if (!data.lastName.trim()) errors.lastName = 'Last name is required';
    if (!data.email.trim()) {
      errors.email = 'Email is required';
    } else if (!EMAIL_PATTERN.test(data.email.trim())) {
      errors.email = 'Please enter a valid email address';
    }
    if (!data.password) {
      errors.password = 'Password is required';
    } else {
      // Same policy as the backend, straight from the shared helper.
      const [firstFailure] = validateNewPassword(data.password, data.password).errors;
      if (firstFailure) errors.password = firstFailure;
    }
  }

  if (step === 2) {
    const legalName = data.companyLegalName.trim();
    if (!legalName) {
      errors.companyLegalName = 'Company legal name is required';
    } else if (legalName.length < 2) {
      errors.companyLegalName = 'Enter at least 2 characters';
    }
    if (!data.country) errors.country = 'Country is required';
  }

  if (step === 3 && !data.acceptTerms) {
    errors.acceptTerms =
      'You must accept the Terms & Conditions and the Privacy Policy to create an account.';
  }

  return errors;
};

/**
 * Self-service sign-up, in three steps: the person, the organization that is
 * the contracting Customer, then the legal checkpoint. All state and all
 * validation live here; the step components are presentational.
 */
const SignupPage: React.FC = () => {
  const navigate = useNavigate();
  const { signup, signupWithGoogle } = useAuth();

  // A Google login that found no workspace lands here: the login button leaves
  // the credential and the verified profile, so the visitor skips the account
  // step and goes straight to the organization. Read into state once (peek is
  // non-destructive, so a StrictMode double render agrees) and cleared on mount.
  const [handoff] = useState(peekGoogleGetStarted);

  const [step, setStep] = useState<SignupStep>(handoff ? 2 : 1);
  const [data, setData] = useState<SignupWizardData>(() =>
    handoff
      ? {
          ...EMPTY_SIGNUP_WIZARD_DATA,
          email: handoff.profile.email,
          firstName: handoff.profile.firstName,
          lastName: handoff.profile.lastName,
        }
      : EMPTY_SIGNUP_WIZARD_DATA
  );
  const [errors, setErrors] = useState<SignupWizardErrors>({});
  const [loading, setLoading] = useState(false);
  /** Raw Google credential, kept across steps 2-3 and posted at the end. */
  const [googleCredential, setGoogleCredential] = useState<string | null>(
    handoff?.credential ?? null
  );
  /**
   * Bumped on every failed validation attempt. A counter rather than a boolean
   * derived from `errors`, because `errors` also changes as the user types — the
   * caret must move only in response to a rejected submit, never mid-typing.
   */
  const [validationFailures, setValidationFailures] = useState(0);

  const formRef = useRef<HTMLFormElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const mountedRef = useRef(false);

  const googleLinked = googleCredential !== null;

  // The hand-off is single use: the credential now lives in this component's
  // state, and nothing should find it again on a later visit.
  useEffect(() => {
    clearGoogleGetStarted();
  }, []);

  // Put the caret on the first field to fix. Without this a rejected Continue
  // leaves focus on the button, so a screen-reader user is told nothing about
  // why nothing happened — unacceptable on the surface that forms the contract.
  useEffect(() => {
    if (validationFailures === 0) return;
    formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
  }, [validationFailures]);

  // Announce the new step and put the caret at the top of it. Skipped on mount
  // so landing on /auth/signup does not steal focus.
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    headingRef.current?.focus();
  }, [step]);

  // Enter always advances/submits, wherever it lands. Two failure modes are
  // covered: focus drifting off the form (body focus -> no implicit
  // submission), and password-manager extensions cancelling the browser's
  // implicit submission outright. So inside our inputs we submit explicitly and
  // preventDefault to avoid double-firing when implicit submission does work.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      const target = e.target as HTMLElement | null;
      if (target && formRef.current?.contains(target)) {
        if (target.tagName === 'INPUT') {
          e.preventDefault();
          formRef.current.requestSubmit();
        }
        return; // buttons/links/selects inside the form keep their native Enter
      }
      // Leave Enter alone wherever it already means something.
      if (target?.closest('form, a, button, [role="dialog"]')) return;
      formRef.current?.requestSubmit();
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, []);

  const handleFieldChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { name, value } = event.target;
      setData((prev) => ({
        ...prev,
        [name]: value,
        // Changing the contracting entity invalidates an already-given
        // acceptance: the sentence names the company, so a tick made for
        // 'Acme Ltd' must not be persisted as agreed on behalf of 'Beta SL'.
        // The user is sent back through step 3 to agree to the new sentence.
        ...(name === 'companyLegalName' && value !== prev.companyLegalName
          ? { acceptTerms: false }
          : null),
      }));
      setErrors((prev) => ({ ...prev, [name]: undefined, general: undefined }));
    },
    []
  );

  const handleConsentChange = useCallback((field: SignupConsentField, checked: boolean) => {
    setData((prev) => ({ ...prev, [field]: checked }));
    setErrors((prev) => ({ ...prev, acceptTerms: undefined, general: undefined }));
  }, []);

  const handleGoogleCredential = useCallback(
    (credential: string, profile: GoogleProfilePrefill) => {
      setGoogleCredential(credential);
      // Prefill is display only — the server re-verifies the token and derives
      // the identity from it. Absent claims keep whatever was already typed,
      // and the password is dropped: this path has no password to send.
      setData((prev) => ({
        ...prev,
        email: profile.email || prev.email,
        firstName: profile.firstName || prev.firstName,
        lastName: profile.lastName || prev.lastName,
        password: '',
      }));
      setErrors({});
      setStep(2);
    },
    []
  );

  const handleUseEmailInstead = useCallback(() => {
    setGoogleCredential(null);
    setErrors({});
  }, []);

  const handleBack = useCallback(() => {
    // Nothing is cleared but the messages: every answer survives the round trip.
    setErrors({});
    setStep((prev) => (prev > 1 ? ((prev - 1) as SignupStep) : prev));
  }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (loading) return;

    if (step < SIGNUP_STEP_TOTAL) {
      const stepErrors = validateStep(step, data, googleLinked);
      if (Object.keys(stepErrors).length > 0) {
        setErrors(stepErrors);
        setValidationFailures((n) => n + 1);
        return;
      }
      setErrors({});
      setStep((prev) => (prev + 1) as SignupStep);
      return;
    }

    // Final step: re-check every step, so jumping back and emptying a field can
    // never post a hole.
    for (const candidate of SIGNUP_STEPS) {
      const stepErrors = validateStep(candidate, data, googleLinked);
      if (Object.keys(stepErrors).length > 0) {
        setErrors(stepErrors);
        setStep(candidate);
        setValidationFailures((n) => n + 1);
        return;
      }
    }

    setLoading(true);
    setErrors({});

    try {
      const workspaceName = data.workspaceName.trim();
      const organization = {
        company_legal_name: data.companyLegalName.trim(),
        country: data.country,
        // Omitted when blank: the server defaults it to the legal name.
        ...(workspaceName ? { workspace_name: workspaceName } : {}),
        ...(data.companySize ? { company_size: data.companySize } : {}),
        accept_terms: data.acceptTerms,
        accept_marketing: data.acceptMarketing,
        accepted_documents: buildAcceptedDocuments(),
      };

      if (googleCredential) {
        await signupWithGoogle({ id_token: googleCredential, ...organization });
      } else {
        await signup({
          email: data.email.trim(),
          password: data.password,
          first_name: data.firstName.trim(),
          last_name: data.lastName.trim(),
          ...organization,
        });
      }
      // Smart home routes the fresh workspace Admin into their resolved view mode.
      navigate('/');
    } catch (err: any) {
      const { errors: mapped, step: target } = resolveSignupApiError(
        err.response?.status,
        err.response?.data
      );
      setErrors(mapped);
      if (target !== null) setStep(target);
    } finally {
      setLoading(false);
    }
  };

  const submitLabel =
    step < SIGNUP_STEP_TOTAL ? 'Continue' : loading ? 'Creating account...' : 'Create account';

  return (
    <AuthLayout>
      <motion.div {...slideUp}>
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-[26px] font-bold text-text-primary mb-2">
            Get Started Absolutely Free
          </h1>
          <p className="text-sm text-text-secondary">
            Already have an account?{' '}
            <Link to="/auth/login" className="text-primary-500 font-medium hover:underline">
              Sign in
            </Link>
          </p>
        </div>

        {handoff && googleLinked && (
          <p
            role="status"
            className="mb-6 rounded-lg bg-primary-50 p-3.5 text-sm text-text-secondary dark:bg-primary-900/10"
          >
            We couldn&apos;t find a workspace for{' '}
            <span className="font-medium text-text-primary">{handoff.profile.email}</span> yet.
            Tell us about your organization to create one.
          </p>
        )}

        <AuthStepIndicator
          current={step}
          total={SIGNUP_STEP_TOTAL}
          label={SIGNUP_STEP_LABELS[step]}
          className="mb-6"
        />

        <h2
          ref={headingRef}
          tabIndex={-1}
          className="mb-5 text-lg font-bold text-text-primary focus:outline-none"
        >
          {SIGNUP_STEP_HEADINGS[step]}
        </h2>

        <form ref={formRef} onSubmit={handleSubmit} className="space-y-5" noValidate>
          {step === 1 && (
            <StepAccount
              data={data}
              errors={errors}
              disabled={loading}
              googleLinked={googleLinked}
              onChange={handleFieldChange}
              onGoogleCredential={handleGoogleCredential}
              onUseEmailInstead={handleUseEmailInstead}
            />
          )}
          {step === 2 && (
            <StepOrganization
              data={data}
              errors={errors}
              disabled={loading}
              onChange={handleFieldChange}
            />
          )}
          {step === 3 && (
            <StepLegal
              data={data}
              errors={errors}
              disabled={loading}
              onConsentChange={handleConsentChange}
            />
          )}

          {/* General Error — above the buttons */}
          {errors.general && (
            <div
              role="alert"
              className="p-3.5 bg-error-50 dark:bg-error-900/10 text-error-600 dark:text-error-400 rounded-lg text-sm"
            >
              <p>{errors.general}</p>
            </div>
          )}

          <div className="flex items-center gap-3">
            {step > 1 && (
              <button
                type="button"
                onClick={handleBack}
                disabled={loading}
                className={BACK_BUTTON_CLASS}
              >
                Back
              </button>
            )}
            <button
              type="submit"
              disabled={loading || (step === SIGNUP_STEP_TOTAL && !data.acceptTerms)}
              className={PRIMARY_BUTTON_CLASS}
            >
              {submitLabel}
            </button>
          </div>
        </form>
      </motion.div>
    </AuthLayout>
  );
};

export default SignupPage;
